import { FastField, useFormikContext } from 'formik';
import _, { get } from 'lodash';
import { FC, useMemo } from 'react';
import { Autocomplete, Chip, MenuItem, TextField } from '@mui/material';
import { InputFieldConditionalRendering } from '../conditional-rendering/InputFieldConditionalRendering';

interface OptionItem {
  value: number | string;
  label: string;
}

interface SelectTextFieldProps {
  label: string;
  fieldName: string;
  formik?: any;
  options: OptionItem[];
  disabled?: boolean;
}

export const DefaultSelectField: FC<SelectTextFieldProps> = ({ label, fieldName, formik, options, disabled }) => {
  return (
    <TextField
      margin="normal"
      label={label}
      {...formik.getFieldProps(fieldName)}
      type="text"
      variant="outlined"
      fullWidth
      disabled={disabled}
      value={_.get(formik.values, fieldName)}
      onChange={formik.handleChange}
      error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
      helperText={(formik.touched[fieldName] && formik.errors[fieldName]) ?? ' '}
      select
      data-cy={fieldName + '-field'}
      style={{ textAlign: 'left' }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} data-cy={fieldName + '-field-' + option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const DefaultFastSelectField: FC<SelectTextFieldProps> = ({ label, fieldName, options, disabled }) => {
  return (
    <InputFieldConditionalRendering fieldName={fieldName}>
      <FastField name={fieldName}>
        {(input: any) => {
          const { field, form } = input;
          return (
            <TextField
              margin="normal"
              label={label}
              variant="outlined"
              fullWidth
              disabled={disabled}
              error={form.touched[fieldName] && Boolean(form.errors[fieldName])}
              helperText={(form.touched[fieldName] && form.errors[fieldName]) ?? ' '}
              select
              data-cy={fieldName + '-field'}
              style={{ textAlign: 'left' }}
              {...field}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value} data-cy={fieldName + '-field-' + option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          );
        }}
      </FastField>
    </InputFieldConditionalRendering>
  );
};

export const DefaultMultiSelectField: FC<SelectTextFieldProps> = ({ label, fieldName, formik, options }) => {
  return (
    <TextField
      margin="normal"
      label={label}
      {...formik.getFieldProps(fieldName)}
      type="text"
      variant="outlined"
      fullWidth
      select
      SelectProps={{
        multiple: true,
      }}
      onChange={formik.handleChange}
      value={formik.values[fieldName]}
      error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
      helperText={(formik.touched[fieldName] && formik.errors[fieldName]) ?? ' '}
      style={{ textAlign: 'left' }}
      data-cy={fieldName + '-field'}
    >
      {options.map((option, index) => (
        <MenuItem key={index} value={option.value} data-cy={fieldName + '-field-' + option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const DefaultAutoCompleteChipField: FC<SelectTextFieldProps> = ({ label, fieldName, options, disabled }) => {
  const formik = useFormikContext() as any;

  const idToNameMap: Record<string, string> = useMemo(() => {
    return options.reduce((acc, option) => {
      return { ...acc, [option.value]: option.label };
    }, {});
  }, [options]);

  return (
    <Autocomplete
      disabled={disabled}
      isOptionEqualToValue={(option: OptionItem, value: string) => option.value === value}
      style={{
        marginTop: '16px',
        marginBottom: '8px',
      }}
      multiple
      disableClearable
      filterSelectedOptions
      {...formik.getFieldProps(fieldName)}
      options={options}
      data-cy={fieldName + '-field'}
      onChange={(event, option: OptionItem[]) => {
        const valOnly = option.map((o) => o?.value ?? o);
        formik.setFieldValue(fieldName, [...valOnly]);
      }}
      filterOptions={(options: OptionItem[], state) => {
        return options.filter((option) => {
          const filterText = state.inputValue.toLowerCase();
          return option.label.toLowerCase().includes(filterText);
        });
      }}
      renderTags={(values: string[], getTagProps) =>
        values.map((value: any, index: number) => {
          return (
            <Chip
              variant="outlined"
              data-cy={fieldName + '-field-delete-' + value}
              label={idToNameMap[value]}
              {...getTagProps({ index })}
              onDelete={() => {
                const currentSelections = get(formik.values, fieldName);
                const deleteIndex = currentSelections.indexOf(value);
                currentSelections.splice(deleteIndex, 1);
                formik.setFieldValue(fieldName, [...currentSelections]);
              }}
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} style={{ cursor: 'pointer' }} label={label} />}
    />
  );
};
